import React, { createContext, useState, useEffect } from "react";
import { UserService } from './UserService';
import { PreferenceService } from './PreferenceService';
import { ParametreService } from './ParametreService';
import { FolderService } from './FolderService';
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router";
import { granted, getDomainFromUrl, setCookie, getCookie } from '../helper/Helper'
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';

const GlobalContext = createContext(undefined);

function GlobalProvider({ children }) {

  const [folders, setFolders] = useState();
  const [folderDetails, setFolderDetails] = useState();
  const [userDetails, setUserDetails] = useState();
  const [preferences, setPreferences] = useState();
  const [parametres, setParametres] = useState();
  const [grants, setGrants] = useState();
  const [apiVersion, setApiVersion] = useState();

  const [globalContextLoaded, setGlobalContextLoaded] = useState(false);
  const [globalContextErrorList, setGlobalContextErrorList] = useState([]);

  const [isDisconnected, setIsDisconnected] = useState(false);
  const [isDbUpdating, setIsDbUpdating] = useState(false);


  let { folderId } = useParams();

  const navigate = useNavigate();

  let location = useLocation();

  useEffect(() => {

    getUser()
    getFolders()

    // broadcast : si une déconnexion eu lieu (appel à Logout page) via un autre onglet du navigateur
    const bcAuthentication = new BroadcastChannel('authentication');
    bcAuthentication.onmessage = function (ev) {
      if (ev.data == "logoutFlag") {
        sessionStorage.removeItem('access_token');
        ["id_token", "refresh_token"].forEach(token =>
          setCookie(token, "", -10, getDomainFromUrl(global.config._FRONT_URL_))
        );
        setIsDisconnected(true)
      }

      if (ev.data === "loginFlag") {
        setIsDisconnected(false)
      }

    }

    // broadcast : erreur 426 ou 423 : mise à jour de l'application
    let checkApiInterval;
    const bcDbUpdate = new BroadcastChannel('dbUpdate');
    bcDbUpdate.onmessage = function (ev) {
      if (ev.data == "updatingFlag") {
        setIsDbUpdating(true)
        window.clearInterval(checkApiInterval)
        checkApiInterval = window.setInterval(function () {
          FolderService.getFolders().then((res) => {
            window.location.reload()
          }).catch(error => { })
        }, 10000);
      }
    }

  }, []);

  useEffect(() => {
    if (folderDetails) {
      getPreferences()
      getParametres()
      getGrants()
    }
  }, [folderDetails]);

  useEffect(() => {
    if (folderDetails && userDetails && preferences && parametres && grants) {
      setGlobalContextLoaded(true)
      setGlobalContextErrorList([])
    } else {
      setGlobalContextLoaded(false)
    }
  }, [folderDetails, userDetails, preferences, parametres, grants]);

  function getFolders() {

    FolderService.getFolders().then((res) => {

      setFolders(res.data.data)
      setApiVersion(res.data.status.api_version)

      /* Define folderDetails */
      let folderFound = res.data.data?.find(f => f.dossier._id == folderId);
      if (folderFound) {
        setFolderDetails(folderFound)
      }

      /* Redirection si le dossier n'a pas été trouvé mais que quelque chose a été saisi dans l'URL */
      if (!folderFound && folderId) {
        navigate(`/error?error-id=folder-not-found`)
      }

      if (res?.data?.data?.length > 0) {
        // Redirection vers les dossiers si aucune page dans l'url
        !location.pathname.split("/")[1] && navigate(`/dossiers`)
      }

    }).catch(error => {
      setFolders()
      setApiVersion()
      setGlobalContextErrorList(prevState => [...prevState, error])
      navigate(`/error?error-id=folder-not-found`)
    });
  }

  function getUser() {
    UserService.getUserInfos()
      .then((res) => {
        setUserDetails(res.data)
      }).catch(error => {
        setUserDetails()
        setGlobalContextErrorList(prevState => [...prevState, error])
      });
  }

  function getPreferences() {
    folderDetails &&
      PreferenceService.getPreferences(folderDetails?.dossier?._id, false, false).then(res => {
        setPreferences(res.data.data.preferences)
      }).catch(error => {
        setPreferences()
        setGlobalContextErrorList(prevState => [...prevState, error])
      });
  }

  function getParametres() {
    folderDetails &&
      ParametreService.getParametres(folderDetails?.dossier?._id, false, false, { params: ["general", "articles", "brouillons", "clients", "contributions", "factures", "familles", "fournisseurs", "proformas", "abonnements", "reglements", "tags", "tarifs"] }).then(res => {
        setParametres(res.data.data.parametres)
      }).catch(error => {
        setParametres()
        setGlobalContextErrorList(prevState => [...prevState, error])
      });
  }

  function getGrants() {
    folderDetails &&
      UserService.getUserGrants(folderDetails?.dossier?._id).then((res) => {
        setGrants(res.data.data.grants)
      }).catch(error => {
        setGrants()
        setGlobalContextErrorList(prevState => [...prevState, error])
      });
  }

  const LogoutAlert = () => {
    return (
      <Modal show={true} centered>
        <Modal.Header className="border-0 pb-0">
          <Modal.Title>Déconnecté</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Vous avez été déconnecté des applications XL.
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="primary" autoFocus onClick={() => UserService.redirectToExternalLogin()}>Se reconnecter</Button>
        </Modal.Footer>
      </Modal>
    )
  }

  const DbUpdateAlert = () => {
    return (
      <Modal show={true} centered>
        <Modal.Header className="border-0 pb-0">
          <Modal.Title>Mise à jour</Modal.Title>
        </Modal.Header>
        <Modal.Body className="mb-3">
          Mise à jour de la base de donnée en cours. Veuillez patienter...
        </Modal.Body>
      </Modal>
    )
  }

  return (
    <GlobalContext.Provider value={{ globalContextLoaded, globalContextErrorList, userDetails, preferences, getPreferences, parametres, getParametres, grants, getGrants, folders, getFolders, folderDetails, setFolderDetails, apiVersion }}>
      {isDisconnected && <LogoutAlert />}
      {isDbUpdating && <DbUpdateAlert />}
      {userDetails && children}
    </GlobalContext.Provider>
  );
}

export { GlobalProvider, GlobalContext };
